import { useMemo } from 'react';
import { StyleSheet, View } from 'react-native';

import { Skeleton } from '@fhs/ui';
import { selectors, useAppSelector } from '@fhs-legacy/frontend/src/state/global-state';
import { ICartEntry } from '@rbi-ctg/menu';

import { calculateItemPrice } from './calculate-item-price';
import { FreePriceDisplay, OfferPriceDisplay, OriginalPriceDisplay } from './item-price-components';

interface ItemPriceProps {
  item: ICartEntry;
  isRewardApplied: boolean;
}

export default function ItemPrice({ item, isRewardApplied }: ItemPriceProps) {
  const { appliedLoyaltyRewards, selectedOffer, offerEligibleItem } = useAppSelector(state => ({
    appliedLoyaltyRewards: selectors.loyalty.selectAppliedLoyaltyRewards(state),
    selectedOffer: selectors.loyalty.selectSelectedOffer(state),
    offerEligibleItem: selectors.loyalty.selectOfferEligibleItem(state, item.cartId),
  }));

  const offersLoading = useAppSelector(selectors.loyalty.selectOffersLoading);

  const priceData = useMemo(
    () =>
      calculateItemPrice(
        item,
        isRewardApplied,
        selectedOffer,
        offerEligibleItem,
        appliedLoyaltyRewards
      ),
    [item, isRewardApplied, selectedOffer, offerEligibleItem, appliedLoyaltyRewards]
  );

  if (offersLoading && offerEligibleItem) {
    return <Skeleton style={styles.pricesSkeleton} />;
  }

  const isOfferEligibleItem = !!offerEligibleItem;

  return (
    <View testID={`item-price-${item.cartId}`} style={styles.prices}>
      <OriginalPriceDisplay
        price={isOfferEligibleItem ? priceData.originalPrice : priceData.finalItemPrice}
        shouldShowStrikeThrough={priceData.shouldShowStrikeThrough}
      />

      {/* if rewards are being used  */}
      <FreePriceDisplay showFree={priceData.isFree && !isOfferEligibleItem} />

      {isOfferEligibleItem && <OfferPriceDisplay price={priceData.finalItemPrice} />}
    </View>
  );
}

const styles = StyleSheet.create({
  prices: {
    alignItems: 'flex-end',
  },
  pricesSkeleton: {
    height: 18,
  },
});
